import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { messagesAPI } from '../../lib/api';
import Layout from '../../components/Layout';
import { motion } from 'framer-motion';
import { MessageSquare, Plus, User, ChevronRight } from 'lucide-react';

export default function CoachMessages() {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const [convRes, playerRes] = await Promise.all([
        messagesAPI.conversations(),
        messagesAPI.players(),
      ]);
      setConversations(convRes.data || []);
      setPlayers(playerRes.data || []);
    } catch {}
    setLoading(false);
  };

  const newPlayers = players.filter(p => !conversations.some(cv => cv.user_id === p.id));

  return (
    <Layout title="MESSAGES" showBack>
      <div className="px-4 py-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-['Barlow_Condensed'] text-xl font-bold tracking-tight flex items-center gap-2" data-testid="coach-messages-title">
            <MessageSquare size={20} className="text-[#2563EB]" /> MESSAGES
          </h2>
          {newPlayers.length > 0 && (
            <button
              onClick={() => setShowNew(!showNew)}
              className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-[#2563EB]/15 text-[#2563EB] px-3 py-1.5 rounded-lg"
              data-testid="coach-new-message-btn"
            >
              <Plus size={12} /> New
            </button>
          )}
        </div>

        {/* New conversation picker */}
        {showNew && newPlayers.length > 0 && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="space-y-2">
            <p className="text-[10px] text-[#64748B] uppercase tracking-widest font-bold">Message a player</p>
            {newPlayers.map(player => (
              <button
                key={player.id}
                onClick={() => navigate(`/coach/messages/${player.id}`)}
                className="w-full flex items-center gap-3 p-3 bg-white border border-[#2563EB]/30 rounded-xl text-left"
                data-testid={`new-conv-${player.id}`}
              >
                <div className="w-10 h-10 rounded-full bg-[#E2E8F0] flex items-center justify-center">
                  <User size={18} className="text-[#64748B]" />
                </div>
                <div className="flex-1">
                  <p className="font-['Barlow_Condensed'] text-sm font-bold">{player.name}</p>
                  <div className="flex items-center gap-2">
                    {player.position && <span className="text-[9px] text-[#00E5FF] uppercase font-bold">{player.position}</span>}
                    <span className={`text-[9px] font-bold ${player.subscription_active ? 'text-[#10B981]' : 'text-[#64748B]'}`}>
                      {player.subscription_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
                <ChevronRight size={16} className="text-[#64748B]" />
              </button>
            ))}
          </motion.div>
        )}

        {loading ? (
          <div className="flex justify-center py-12"><div className="spinner" /></div>
        ) : conversations.length === 0 && !showNew ? (
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-8 text-center">
            <MessageSquare size={32} className="mx-auto text-[#1C2B54] mb-3" />
            <p className="text-[#64748B] text-sm">No conversations yet.</p>
            {newPlayers.length > 0 && (
              <button
                onClick={() => setShowNew(true)}
                className="mt-3 text-[#2563EB] text-xs font-bold uppercase tracking-wider"
                data-testid="start-first-coach-msg"
              >
                Message a player
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {conversations.map((conv, i) => (
              <motion.button
                key={conv.user_id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                onClick={() => navigate(`/coach/messages/${conv.user_id}`)}
                className="w-full flex items-center gap-3 p-3 bg-white border border-[#E2E8F0] rounded-xl text-left hover:border-[#2563EB]/30 transition-colors"
                data-testid={`coach-conversation-${i}`}
              >
                <div className="w-10 h-10 rounded-full bg-[#1C2B54] flex items-center justify-center flex-shrink-0">
                  <User size={18} className="text-[#64748B]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-['Barlow_Condensed'] text-sm font-bold truncate">{conv.name}</p>
                    {conv.position && <span className="text-[8px] text-[#00E5FF] font-bold uppercase">{conv.position}</span>}
                  </div>
                  <p className="text-xs text-[#64748B] truncate mt-0.5">{conv.last_message}</p>
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <span className="text-[9px] text-[#64748B]">
                    {conv.last_at ? new Date(conv.last_at).toLocaleDateString([], { month: 'short', day: 'numeric' }) : ''}
                  </span>
                  {conv.unread_count > 0 && (
                    <span className="bg-[#2563EB] text-white text-[9px] font-bold w-5 h-5 rounded-full flex items-center justify-center" data-testid={`coach-unread-${i}`}>
                      {conv.unread_count}
                    </span>
                  )}
                </div>
              </motion.button>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
